/**
 * The parts of the composer that differ by moderation mode: the standing
 * explanation of how a space is moderated, the admin's bypass notice, the
 * post button's wording, and post-anyway's second-look modal.
 *
 * A space's mode is a first-class field of the space resource, read here
 * through `useSpaceModerationMode`, so each of these is a plain conditional
 * on `ModerationMode | null`. `null` means the space is still loading, and
 * none of them says anything for it.
 *
 * The server decides what actually happens to a post
 * (`server/modules/comments.ts`); these only tell the author what to expect.
 */

import { ModerationMode } from '@repo/shared/moderation-modes';
import { AITag } from 'component/aitag';
import { AdminBanner } from 'component/banner/admin-banner';
import { ModerationInfoBanner } from 'component/banner/moderation-info-banner';
import { ModerationInfoBannerLoader } from 'component/banner/moderation-info-banner-loader';
import type { CommentRejection, DraftComment } from 'component/comment';
import { TakeSecondLookModal } from 'component/modal/moderation/take-second-look-modal';
import { ManualModerationTag } from 'component/tag/manually-moderated-tag';
import { useTranslate } from 'component/translation';
import { useSpaceModerationMode } from 'data/spaces';
import type { ReactElement, ReactNode } from 'react';
import { Catcher } from 'system/catcher';
import { useIsAdmin } from 'util/admin';
import type { TLabel } from 'util/stdtypes';
import { t } from 'util/stdtypes';

import { ModerationReport } from './moderation-report';

/**
 * What the composer shows below the text being edited: the report of a
 * moderation decision about this post, then the mode's explanation of how the
 * space is moderated.
 *
 * A report that refuses the post stands the explanation down — telling the
 * author how moderation works while showing them it just blocked them reads
 * as a lecture. A report that only says the post is still with a moderator
 * leaves it up, since the author may yet edit and resubmit.
 *
 * Returns the two as siblings so the composer's own spacing separates them.
 */
export function ModerationReportAndExplanation({
    comment,
    loading,
    onEditingDone,
    rejection,
}: {
    comment: DraftComment;
    loading?: boolean;
    onEditingDone?: () => void;
    rejection?: CommentRejection | null;
}) {
    const mode = useSpaceModerationMode();
    const refused = isPostRefused({ comment, rejection });
    return (
        <>
            <Catcher>
                <ModerationReport comment={comment} onEditingDone={onEditingDone} rejection={rejection} />
            </Catcher>
            <Catcher>{refused ? null : <ModerationModeExplanation loading={loading} mode={mode} />}</Catcher>
        </>
    );
}

/**
 * Whether moderation refused this post: either this save was refused (by the
 * AI, or because moderation was unavailable), or a moderator rejected it
 * earlier. A post still waiting for a moderator is not refused.
 */
export function isPostRefused({ comment, rejection }: { comment?: DraftComment | null; rejection?: CommentRejection | null }): boolean {
    return !!(rejection || comment?.moderationState?.rejected);
}

function ModerationModeExplanation({ loading, mode }: { loading?: boolean; mode: ModerationMode | null }) {
    if (mode === ModerationMode.MANUAL_MODERATION) return <ManualModerationNotice />;
    return <AiModerationNotice loading={loading} mode={mode} />;
}

function ManualModerationNotice() {
    return (
        <ModerationInfoBanner
            description={t(
                'moderation.info_manual_moderation_banner_text',
                'To maintain a welcoming and respectful environment, all posts in this space are manually moderated before publishing to meet the [Community Guidelines]($onPress)',
            )}
            Tag={<ManualModerationTag />}
        />
    );
}

function AiModerationNotice({ loading, mode }: { loading?: boolean; mode: ModerationMode | null }) {
    const content = aiModerationNoticeContent(mode);
    if (!content) return null;
    return (
        <ModerationInfoBannerLoader loading={loading}>
            <ModerationInfoBanner description={content.description} Tag={content.Tag} />
        </ModerationInfoBannerLoader>
    );
}

function aiModerationNoticeContent(mode: ModerationMode | null): { description: TLabel; Tag: ReactNode } | null {
    switch (mode) {
        case ModerationMode.AI_MODERATION:
        case ModerationMode.AI_MODERATION_WITH_POST_ANYWAY:
            return {
                description: t(
                    'moderation.info_ai_moderation_banner_text',
                    'To maintain a welcoming and respectful environment, this space uses AI to help moderate comments. AI may flag your post for your additional review if it does not follow the [Community Guidelines]($onPress)',
                ),
                Tag: <AITag />,
            };
        case ModerationMode.AI_AND_MANUAL_MODERATION:
            return {
                description: t(
                    'moderation.info_ai_and_manual_moderation_banner_text',
                    'To maintain a welcoming and respectful environment, all posts in this space are both AI and manually moderated before publishing to ensure they follow the [Community Guidelines]($onPress)',
                ),
                Tag: <AITag label={t('moderation.ai_and_manual_label', 'AI and manually moderated')} />,
            };
        case ModerationMode.MANUAL_MODERATION:
        case null:
            return null;
    }
}

/**
 * The heads-up an admin gets in the composer: this space is moderated, and you
 * will post straight past it. `AdminBanner` self-gates, so this renders
 * nothing for everyone else.
 */
export function ModerationAdminBypassBanner() {
    const mode = useSpaceModerationMode();
    const translate = useTranslate();
    const label = adminBypassLabel(mode);
    return label ? <AdminBanner text={translate(label)} /> : null;
}

function adminBypassLabel(mode: ModerationMode | null): TLabel | null {
    switch (mode) {
        case ModerationMode.AI_MODERATION:
        case ModerationMode.AI_MODERATION_WITH_POST_ANYWAY:
            return t(
                'moderation.admin_bypass_ai_moderation',
                'This space has AI moderation enabled, but you will automatically bypass it as an Admin user.',
            );
        case ModerationMode.MANUAL_MODERATION:
            return t(
                'moderation.admin_bypass_manual_moderation',
                'This space has human moderation enabled, but you will automatically bypass it as an Admin user.',
            );
        case ModerationMode.AI_AND_MANUAL_MODERATION:
            return t(
                'moderation.admin_bypass_ai_and_manual_moderation',
                'This space has AI and human moderation enabled, but you will automatically bypass both as an Admin user.',
            );
        case null:
            return null;
    }
}

/**
 * The post button's label where the mode changes what pressing it does, and
 * `null` where it doesn't — the composer's own Post/Update wording stands in
 * for that.
 *
 * A mode that queues the post for a human says Submit rather than Post,
 * because nothing appears on pressing it. Post-anyway says so only on a reply
 * the AI has raised a minor violation on, the one time the button publishes
 * something the author has already been warned about. A top-level post keeps
 * its plain wording, because the second-look modal already asked that author
 * to reconsider.
 */
export function useModerationPostActionLabel({
    inProgress,
    isNewComment,
    comment,
    rejection,
}: {
    inProgress: boolean;
    isNewComment: boolean;
    comment: DraftComment;
    rejection?: CommentRejection | null;
}): TLabel | null {
    const mode = useSpaceModerationMode();
    switch (mode) {
        case ModerationMode.MANUAL_MODERATION:
        case ModerationMode.AI_AND_MANUAL_MODERATION:
            if (inProgress) {
                return isNewComment ? t('post.submitting', 'Submitting...') : t('post.updating', 'Updating...');
            }
            return isNewComment ? t('post.submit', 'Submit') : t('post.update', 'Update');
        case ModerationMode.AI_MODERATION_WITH_POST_ANYWAY:
            if (!rejection?.minorViolation || !comment.parentKey) return null;
            if (inProgress) {
                return isNewComment ? t('post.posting_anyway', 'Posting anyway...') : t('post.updating_anyway', 'Updating anyway...');
            }
            return isNewComment ? t('post.post_anyway_button', 'Post anyway') : t('post.update_anyway_button', 'Update anyway');
        case ModerationMode.AI_MODERATION:
        case null:
            return null;
    }
}

type RejectionInterceptModal = (args: {
    comment: DraftComment;
    rejection: CommentRejection;
    isNewComment: boolean;
    post: () => void;
    cancel: () => void;
}) => ReactElement | null;

/**
 * The modal the composer shows on a rejection before reporting it inline, or
 * `null` where the mode has none. Its `post` confirms the rejected draft
 * as-is; `cancel` dismisses to the inline report.
 *
 * Only post-anyway has one, and never for an admin: an admin's rejected post
 * is auto-confirmed by the bypass flow, so a modal offering to edit would
 * post it regardless.
 */
export function useRejectionInterceptModal(): RejectionInterceptModal | null {
    const mode = useSpaceModerationMode();
    const viewerIsAdmin = !!useIsAdmin();
    if (mode !== ModerationMode.AI_MODERATION_WITH_POST_ANYWAY || viewerIsAdmin) return null;
    return takeSecondLookModal;
}

/**
 * The speed bump: a minor-violation post pauses behind a reconsider modal
 * rather than going straight to the inline report.
 *
 * Fires on every attempt the author makes on their own top-level post, a first
 * one and each later edit alike — on a question they get one answer, so gating
 * it to a first post would show it once per space and never again. A reply
 * keeps the inline report.
 */
const takeSecondLookModal: RejectionInterceptModal = ({ comment, rejection, isNewComment, post, cancel }) => {
    if (!rejection.minorViolation || comment.parentKey) return null;
    return <TakeSecondLookModal isNewComment={isNewComment} onClose={cancel} onConfirm={post} />;
};
